import {Box} from "@mui/material";
import type {TFunction} from "i18next";
import React, {useMemo, useRef, useState} from "react";
import SigningButton from "./SigningButton";
import SigningFooter from "./SigningFooter";
import SigningLayout, {consentTextOf} from "./SigningLayout";
import SigningSignaturePad, {SigningSignaturePadHandle, SignatureInk} from "./SigningSignaturePad";
import SigningSubmitError from "./SigningSubmitError";
import {signingFormat} from "./format.util";
import {DigitalSigningFailureReason, DigitalSigningSession} from "./digitalSigning.types";
import {grandTotalCents, initialTipChoice, NO_TIP, resolveTip, TipChoice, tipKey, tipRequest} from "./tip.util";
import TipSelector, {tipOffered} from "./TipSelector";
import {useSigningSubmit} from "./useSigningSubmit";

interface Props {
    token: string;
    session: DigitalSigningSession;
    language: string;
    t: TFunction;
    onLanguageChange: (language: string) => void;
    onSigned: () => void;
    onGone: (reason: DigitalSigningFailureReason) => void;
    fill?: boolean;
}

const EMPTY_INK: SignatureInk = {hasStrokes: false, enough: false};

function DigitalSigningForm({token, session, language, t, onLanguageChange, onSigned, onGone, fill}: Props) {
    const {snapshot} = session;
    const tip = tipOffered(snapshot.tip) ? snapshot.tip : undefined;
    const languages = snapshot.languages?.length
        ? snapshot.languages
        : [snapshot.language].filter((code): code is string => !!code);
    const format = useMemo(
        () => signingFormat(language, snapshot.currency, snapshot.timeZone),
        [language, snapshot]
    );

    const [choice, setChoice] = useState<TipChoice>(() => initialTipChoice(tip));
    const [customInput, setCustomInput] = useState('');
    const [ink, setInk] = useState<SignatureInk>(EMPTY_INK);
    const [tipChanged, setTipChanged] = useState(false);

    const padRef = useRef<SigningSignaturePadHandle>(null);
    const actionRef = useRef<HTMLDivElement>(null);
    const {submit, submitting, failure, failedAttempts, resetFailures} = useSigningSubmit({
        token,
        snapshotSha256: session.snapshotSha256,
        language,
        onSigned,
        onGone,
    });

    const resolvedTip = tip ? resolveTip(tip, choice, customInput) : NO_TIP;
    const canSubmit = !!resolvedTip && ink.enough && !submitting;

    const changeTip = (nextChoice: TipChoice, nextCustomInput: string) => {
        const changed = tipKey(resolvedTip) !== tipKey(resolveTip(tip, nextChoice, nextCustomInput));
        setChoice(nextChoice);
        setCustomInput(nextCustomInput);
        if (!changed) return;

        setTipChanged(ink.hasStrokes);
        padRef.current?.clear();
        setInk(EMPTY_INK);
        // The error was about the signature just cleared: only the notice to sign again is left
        resetFailures();
    };

    const changeInk = (next: SignatureInk) => {
        setInk(next);
        if (next.hasStrokes) setTipChanged(false);
    };

    const send = () => {
        if (!canSubmit || !resolvedTip) return;

        const signaturePng = padRef.current?.toDataUrl();
        if (signaturePng) void submit(tipRequest(resolvedTip), signaturePng);
    };

    return (
        <form
            noValidate
            onSubmit={event => {
                event.preventDefault();
                if (canSubmit) send();
            }}
        >
            <SigningLayout
                snapshot={snapshot}
                format={format}
                t={t}
                language={language}
                fill={fill}
                grandTotalCents={grandTotalCents(snapshot.totals.grossCents, resolvedTip)}
                tip={!!tip && (
                    <TipSelector
                        tip={tip}
                        choice={choice}
                        customInput={customInput}
                        onChange={changeTip}
                        format={format}
                        language={language}
                        t={t}
                        disabled={submitting}
                    />
                )}
                signature={(
                    <SigningSignaturePad
                        ref={padRef}
                        t={t}
                        disabled={submitting || !resolvedTip}
                        disabledMessage={!resolvedTip ? t('Please enter the tip amount first.') : undefined}
                        notice={tipChanged ? t('The tip changed. Please sign again.') : undefined}
                        onInkChange={changeInk}
                        endRef={actionRef}
                    />
                )}
                consent={consentTextOf(snapshot, t, language)}
                action={(
                    <Box ref={actionRef}>
                        <SigningButton type={'submit'} busy={submitting} disabled={!canSubmit}>
                            {t('Send confirmation')}
                        </SigningButton>
                        {!!failure && (
                            <SigningSubmitError
                                failure={failure}
                                failedAttempts={failedAttempts}
                                canRetry={canSubmit}
                                onRetry={send}
                                t={t}
                            />
                        )}
                    </Box>
                )}
                footer={(
                    <SigningFooter
                        hotel={snapshot.hotel}
                        t={t}
                        language={language}
                        languages={languages}
                        onLanguageChange={onLanguageChange}
                        disabled={submitting}
                    />
                )}
            />
        </form>
    );
}

export default DigitalSigningForm;
